import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export type BackNavConfig = {
  primaryText: string;
  targetPath: string;
};

const DEFAULT_CONFIG: BackNavConfig = { primaryText: 'My Trips', targetPath: '/' };

/**
 * Shared hook for back navigation with replace: true to avoid browser history issues.
 * Uses explicit target paths (not history.back()), so direct URLs and empty history are handled.
 *
 * Why replace: true?
 * - Avoids stacking duplicate entries (e.g. Home → Account → Edit Profile → Back → Back
 *   would leave two "Account" entries). Replace keeps history predictable: Back always
 *   goes to the intended parent (e.g. /account or /), not "previous history entry."
 * - Users who expect "back to previous page" still get that semantically (we navigate to
 *   the logical parent), without depending on history length or order.
 *
 * @param getConfig - (pathname, params) => { primaryText, targetPath }
 * @returns primaryText, handleBack. If getConfig throws, returns default (My Trips -> /).
 */
export function useBackNavigation(
  getConfig: (pathname: string, params: Record<string, string | undefined>) => BackNavConfig,
): { primaryText: string; handleBack: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { primaryText, targetPath } = useMemo(() => {
    try {
      return getConfig(location.pathname, params);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console -- intentional dev-only diagnostic
        console.warn('[useBackNavigation] getConfig failed, using default', error);
      }
      return DEFAULT_CONFIG;
    }
  }, [getConfig, location.pathname, params]);

  const handleBack = useCallback(() => {
    try {
      navigate(targetPath, { replace: true });
    } catch (error) {
      // Error boundaries don't catch event-handler errors; ensure user isn't stuck.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console -- intentional dev-only diagnostic
        console.error('[useBackNavigation] navigate failed', error);
      }
      try {
        navigate('/', { replace: true });
      } catch {
        window.location.href = '/';
      }
    }
  }, [navigate, targetPath]);

  return { primaryText, handleBack };
}
